import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';

const DocumentationContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 5% 120px; /* Padding inférieur augmenté pour la navigation */
  position: relative;
  overflow: hidden;
`;

const CenteredContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  margin: auto 0;
`;

const CardsContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 80px; /* Augmentation de l'espacement entre les cartes */
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 60px auto 0; /* Ajout d'une marge supérieure pour compenser la suppression du titre */
  position: relative;
  z-index: 2;
`;

const Card = styled(motion.div)`
  width: 400px; /* Augmentation de la largeur */
  height: 500px; /* Augmentation de la hauteur */
  background: rgba(40, 40, 40, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 40px; /* Augmentation du padding */
  transition: all 0.4s ease;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    background: rgba(50, 50, 50, 0.9);
    border-color: rgba(255, 255, 255, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.1) 0%, 
      rgba(255, 255, 255, 0) 100%);
    z-index: 0;
  }
`;

const PulsatingBorder = styled(motion.div)`
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 22px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  z-index: -1;
  pointer-events: none;
`;

const CardContent = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CardTop = styled.div`
  text-align: center;
`;

const LanguageLabel = styled.h2`
  font-size: 2.2rem; /* Augmentation de la taille de la police */
  font-weight: 600;
  margin-bottom: 15px;
  background: linear-gradient(to right, #ffffff, #cccccc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const LanguageIcon = styled.div`
  font-size: 3.2rem; /* Augmentation de la taille */
  margin-bottom: 20px;
`;

const DocumentIcon = styled(motion.div)`
  width: 130px; /* Augmentation de la taille */
  height: 170px; /* Augmentation de la taille */
  background: #ffffff;
  border-radius: 8px;
  margin: 30px auto; /* Augmentation de la marge */
  position: relative;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30px;
    height: 30px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 0 0 0 8px;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    height: 12px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    box-shadow: 0 35px 0 rgba(0, 0, 0, 0.1), 0 70px 0 rgba(0, 0, 0, 0.1), 0 105px 0 rgba(0, 0, 0, 0.1);
  }
`;

const CardBottom = styled.div`
  text-align: center;
`;

const ViewButton = styled(motion.div)`
  background: rgba(255, 255, 255, 0.15);
  padding: 14px 28px; /* Augmentation du padding */
  border-radius: 30px;
  font-size: 1.1rem; /* Augmentation de la taille de la police */
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  ${Card}:hover & {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.15));
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  }
`;

const ArrowIcon = styled(motion.span)`
  display: inline-block;
  margin-left: 10px;
  font-size: 1.3rem; /* Augmentation de la taille */
`;

const Documentation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
        delay: custom * 0.2,
      }
    }),
  };

  const iconVariants = {
    hover: {
      y: [0, -8, 0],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        repeatType: "loop",
      }
    }
  };

  const pulsateVariants = {
    initial: { opacity: 0.5 },
    animate: {
      opacity: [0.2, 0.5, 0.2],
      scale: [1, 1.02, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
      }
    }
  };

  const arrowVariants = {
    initial: { x: 0 },
    hover: {
      x: [0, 5, 0],
      transition: {
        duration: 0.8,
        repeat: Infinity,
        repeatType: "loop",
      }
    }
  };

  const handleCardClick = (lang) => {
    navigate(`/documentation/${lang}`);
  };

  return (
    <PageLayout>
      <DocumentationContainer>
        <CenteredContent>
          {/* Le titre "Documentation" a été supprimé ici */}
          
          <CardsContainer
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: { staggerChildren: 0.3 }
              }
            }}
          >
            {/* English Documentation Card */}
            <Card 
              as={motion.div}
              custom={0}
              variants={cardVariants}
              whileHover="hover"
              onClick={() => handleCardClick('en')}
            >
              <PulsatingBorder 
                variants={pulsateVariants}
                initial="initial"
                animate="animate"
              />
              <CardContent>
                <CardTop>
                  <LanguageIcon>🇺🇸</LanguageIcon>
                  <LanguageLabel>English</LanguageLabel>
                  <DocumentIcon 
                    variants={iconVariants}
                  />
                </CardTop>
                <CardBottom>
                  <ViewButton>
                    View Documentation
                    <ArrowIcon variants={arrowVariants}>→</ArrowIcon>
                  </ViewButton>
                </CardBottom>
              </CardContent>
            </Card>
            
            {/* Chinese Documentation Card */}
            <Card 
              as={motion.div}
              custom={1}
              variants={cardVariants}
              whileHover="hover"
              onClick={() => handleCardClick('zh')}
            >
              <PulsatingBorder 
                variants={pulsateVariants}
                initial="initial"
                animate="animate"
              />
              <CardContent>
                <CardTop>
                  <LanguageIcon>🇨🇳</LanguageIcon>
                  <LanguageLabel>中文</LanguageLabel>
                  <DocumentIcon 
                    variants={iconVariants}
                  />
                </CardTop>
                <CardBottom>
                  <ViewButton>
                    查看文档
                    <ArrowIcon variants={arrowVariants}>→</ArrowIcon>
                  </ViewButton>
                </CardBottom>
              </CardContent>
            </Card>
          </CardsContainer>
        </CenteredContent>
      </DocumentationContainer>
    </PageLayout>
  );
};

export default Documentation;